/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Sync types and modes", description = "Learn more about different types of syncs and update modes, including upsert, update, insert, and more.", lastUpdatedDate = new Date(1787184000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Sync types and modes",
        description: "Learn more about different types of syncs and update modes, including upsert, update, insert, and more.",
        lastUpdatedDate: "Thu Aug 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Sync types",
          "depth": 0,
          "id": "sync-types"
        }, {
          "value": "Sync modes",
          "depth": 0,
          "id": "sync-modes"
        }, {
          "value": "Sync modes and change data capture",
          "depth": 1,
          "id": "sync-modes-and-change-data-capture"
        }, {
          "value": "Sync type and mode combinations",
          "depth": 0,
          "id": "sync-type-and-mode-combinations"
        }, {
          "value": "Event syncs",
          "depth": 1,
          "id": "event-syncs"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      em: "em",
      strong: "strong",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      ul: "ul",
      li: "li",
      h2: "h2",
      h3: "h3",
      code: "code"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: _jsxs(_components.em, {
          children: ["A sync's ", _jsx(_components.strong, {
            children: "type"
          }), " is what you send to a destination; its ", _jsx(_components.strong, {
            children: "mode"
          }), " is how the destination applies it."]
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams configuring how a sync writes to a destination"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["When ", _jsx(_components.a, {
          href: "/syncs/overview#configuration",
          children: "configuring a sync"
        }), " there are two key settings that determine the basic behavior of your sync:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Sync type"
        }), "\n", _jsx(_components.li, {
          children: "Sync mode"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The options for both vary depending on the destination. Refer to the specific destination documentation for more information about its supported sync types and modes."
      }), "\n", _jsx(_components.h2, {
        id: "sync-types",
        children: "Sync types"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "sync type"
        }), " refers to ", _jsx(_components.em, {
          children: "what"
        }), " you are syncing in a destination. These are the most common types:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Objects"
          }), ": such as customers, accounts, organizations, catalog items, and the objects' associated attributes or metadata"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Events"
          }), ", ", _jsx(_components.strong, {
            children: "Actions"
          }), ", and ", _jsx(_components.strong, {
            children: "Activities"
          }), ": such as conversion events"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Segments"
          }), ", ", _jsx(_components.strong, {
            children: "Audiences"
          }), ", and ", _jsx(_components.strong, {
            children: "Lists"
          }), ": such as subscription or campaign lists, specifically user membership in these lists"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "When syncing to CRM type destinations, it's common to use object type records. When syncing to advertising or marketing platforms, it's common to use objects, events, or lists."
      }), "\n", _jsxs(_components.p, {
        children: ["For some destinations, you can also ", _jsx(_components.a, {
          href: "/destinations/sfmc#managing-user-journeys",
          children: "manage journeys"
        }), ", ", _jsx(_components.a, {
          href: "/destinations/marketo#campaign-trigger",
          children: "trigger campaigns"
        }), ", or ", _jsx(_components.a, {
          href: "/destinations/braze#merge-anonymous-and-known-users",
          children: "merge anonymous and known users"
        }), " by syncing data. Check out a destination's dedicated docs to learn its supported sync types."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsxs(_components.p, {
        children: ["Depending on your sync type, you may have to select your ", _jsx(_components.strong, {
          children: "sync mode"
        }), ". For example, if you're updating contacts, you might have the option to ", _jsx(_components.strong, {
          children: "Insert"
        }), ", ", _jsx(_components.strong, {
          children: "Update"
        }), ", or ", _jsx(_components.strong, {
          children: "Upsert"
        }), " them."]
      }), "\n", _jsxs(_components.p, {
        children: ["Also called update mode, this configuration describes ", _jsx(_components.em, {
          children: "how"
        }), " you want to update records in your destination based on your ", _jsx(_components.a, {
          href: "/models/creating-models",
          children: "model's"
        }), " query results. For example, if your model queries a ", _jsx(_components.strong, {
          children: "Users table"
        }), " and rows in that table change, your sync mode determines how those changes should translate to your destination."]
      }), "\n", _jsx(_components.p, {
        children: "Depending on the destination and sync type, the supported sync modes could include:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update"
          }), ": only update existing records in your destination"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), ": only insert new records without touching existing records"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), ": update existing records and insert new ones"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Add"
          }), ": add records, usually user records in a segment, list, or journey"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Remove"
          }), ": remove records, usually user records in a segment, list, or journey"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Archive"
          }), ": archive the records in your model's query results, where the destination supports it"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "All"
          }), ": overwrites all existing records in your destination with the results of your model query; sometimes called ", _jsx(_components.strong, {
            children: "Mirror"
          }), " mode"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Snapshot"
          }), ": creates a new file on each sync run with the full results set of your model query"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Diff"
          }), ": creates three new files on each sync run—one for rows added, one for rows changed, and another for rows removed since the last sync"]
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Archive"
          }), " is destructive and its behavior is destination-specific. Some\ndestinations require delete permissions to archive records — Salesforce, for\nexample. Confirm what archiving does in your destination's documentation\nbefore using it."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Some sync types such as triggering campaigns, merging users, or sending messages don't require you to select a mode since the sync applies an action rather than updates records. Other sync types, such as ", _jsx(_components.a, {
          href: "#event-syncs",
          children: "events"
        }), ", have only one sync mode option."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "All"
          }), " and ", _jsx(_components.strong, {
            children: "Snapshot"
          }), " sync modes, the order of rows and columns in\nexported files may differ from how they appear in your model. This is normal\nbehavior. See ", _jsx(_components.a, {
            href: "/models/creating-models#row-and-column-ordering",
            children: "row and column\nordering"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "sync-modes-and-change-data-capture",
        children: "Sync modes and change data capture"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/syncs/cdc",
          children: "Change data capture (CDC)"
        }), " is a process that captures changes made to your source data and that records those changes. Specifically, CDC tracks rows that were added to, changed, or removed from your model's query results since the last sync run."]
      }), "\n", _jsx(_components.p, {
        children: "Your sync mode determines how Hightouch acts on observed changes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "update"
          }), " mode, Hightouch only acts on changes to existing rows."]
        }), "\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "insert"
          }), " mode, Hightouch only syncs new rows—those whose ", _jsx(_components.a, {
            href: "/models/creating-models#unique-primary-key-requirement",
            children: "primary key"
          }), " wasn't present in the previous sync run."]
        }), "\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "upsert"
          }), " mode, Hightouch acts on existing ", _jsx(_components.em, {
            children: "and"
          }), " new rows."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Archive"
          }), " and ", _jsx(_components.strong, {
            children: "all"
          }), " modes don't perform change data capture and don't store requests in the ", _jsx(_components.a, {
            href: "/syncs/debugger",
            children: "live debugger"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Sync mode is separate from ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), ", which determines how to treat removed rows—those that leave your model's query results."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-type-and-mode-combinations",
        children: "Sync type and mode combinations"
      }), "\n", _jsx(_components.p, {
        children: "Certain destination types tend to support certain sync modes."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Destination type"
            }), _jsx(_components.th, {
              children: "Example destinations"
            }), _jsx(_components.th, {
              children: "Commonly supported sync modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "CRMs and other object-based destinations"
            }), _jsx(_components.td, {
              children: "Salesforce, HubSpot, Braze, Iterable"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Advertising"
            }), _jsx(_components.td, {
              children: "Google Ads, Meta Conversions API, TikTok, Amazon Ads"
            }), _jsxs(_components.td, {
              children: ["Insert for events, ", _jsx("br", {}), "Update or Upsert for customer segments, lists, and audiences"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "File storage"
            }), _jsx(_components.td, {
              children: "Google Drive, Amazon S3, Azure Blob"
            }), _jsx(_components.td, {
              children: "Insert, All, Diff"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Check out Hightouch's ", _jsx(_components.a, {
          href: "https://hightouch.com/integrations",
          children: "integration catalog"
        }), " for a complete list of destinations and categories."]
      }), "\n", _jsx(_components.h3, {
        id: "event-syncs",
        children: "Event syncs"
      }), "\n", _jsxs(_components.p, {
        children: ["Event syncs are ", _jsx(_components.strong, {
          children: "insert"
        }), " only, meaning that you can't update previously synced events or activities.\nHightouch follows this convention because events tend to be part of ", _jsx(_components.a, {
          href: "https://allthingssql.com/fact-table-vs-dimension-table/",
          children: "fact tables"
        }), " rather than dimension tables.\nFact tables store transactions as rows that shouldn't change."]
      }), "\n", _jsxs(_components.p, {
        children: ["Recall that in ", _jsx(_components.a, {
          href: "#sync-modes-and-change-data-capture",
          children: "insert mode"
        }), ", Hightouch syncs rows whose ", _jsx(_components.a, {
          href: "/models/creating-models#unique-primary-key-requirement",
          children: "primary key"
        }), " wasn't present in the previous sync run.\nIf the event sync's associated model has a primary key that appears in multiple events—for example, ", _jsx(_components.code, {
          children: "user_id"
        }), "—syncs will omit some events."]
      }), "\n", _jsxs(_components.p, {
        children: ["To ensure syncs include all events, select a truly unique column, for example a unique ", _jsx(_components.code, {
          children: "eventID"
        }), ".\nIf your events data doesn't contain an inherently unique column, define a ", _jsx(_components.a, {
          href: "/models/creating-models#unique-primary-key-requirement",
          children: "composite primary key"
        }), " during model creation."]
      })]
    });
  }
}
export default MDXContent;
